/*
 * File: SaveButton.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 13 Jan 2022 6:18:45 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Dropdown, message, Tooltip } from 'antd';
import { useFormikContext } from 'formik';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { FC, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setSubmitLoadingMessage } from 'redux/salesOrder/ordersSlice';
import { setDraftId } from 'redux/salesOrder/salesOrderDraftIdSlice';
import { salesOrderApi, useCreateAttachmentMutation, useCreateSalesOrderMutation, useUpdateSalesOrderMutation } from 'redux/services/useMamaBearService';

export const SaveButton: FC = () => {
  const { isSubmitting, values } = useFormikContext<SalesOrderPayloadModel>();

  const { pathname } = useLocation();
  const { id: salesOrderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createAttachment] = useCreateAttachmentMutation();
  const [updateSalesOrder, { isLoading: isUpdating }] = useUpdateSalesOrderMutation({ fixedCacheKey: salesOrderId });
  const [createSalesOrder, { isLoading }] = useCreateSalesOrderMutation({ fixedCacheKey: 'newOrder' });

  /* ****************** Functions ****************** */

  const currentLocation = useMemo(() => {
    if (pathname.includes('header-info')) {
      return 'header-info';
    } else if (pathname.includes('delivery-address')) {
      return 'delivery-address';
    } else if (pathname.includes('delivery-details')) {
      return 'delivery-details';
    } else if (pathname.includes('products/search')) {
      return 'products/search';
    } else if (pathname.includes('products')) {
      return 'products';
    } else if (pathname.includes('overview')) {
      return 'overview';
    } else {
      return '';
    }
  }, [pathname]);

  const handleSaveSalesOrder = async (close?: boolean): Promise<void> => {
    dispatch(setSubmitLoadingMessage({ message: 'Creating sales order', addCount: false }))
    try {
      const savedSalesOrder = await createSalesOrder({ payload: values }).unwrap();
      const { draftId, id } = savedSalesOrder;
      const formData = new FormData();

      if (values.attachments) {
        for (const attachment of values.attachments) {
          formData.append(attachment.name, attachment);

          await createAttachment({ file: formData, salesOrderId: id });

          formData.delete(attachment.name);
        }
      }

      if (draftId) dispatch(setDraftId(draftId));
      dispatch(setSubmitLoadingMessage({ message: '', addCount: false }))
      dispatch(salesOrderApi.util.invalidateTags(['SalesOrder', 'SalesOrders']));
      navigate(`/drafts/${id}/${currentLocation}`);

      if (close) {
        navigate('/');
      }
    } catch (err) {
      message.error((err as { data: { errorMessage: string } }).data.errorMessage ?? 'Failed to save sales order.', 5);
    }
  };

  const handleUpdateSalesOrder = async (close?: boolean): Promise<void> => {
    dispatch(setSubmitLoadingMessage({ message: 'Saving sales order', addCount: false }))

    try {
      const formData = new FormData();

      const updatedSalesOrder = await updateSalesOrder({ payload: values, salesOrderId: salesOrderId || '' }).unwrap();

      if (values.attachments) {
        for (const attachment of values.attachments) {
          formData.append(attachment.name, attachment);

          await createAttachment({ file: formData, salesOrderId: updatedSalesOrder.id }).unwrap();


          formData.delete(attachment.name);
        }
      }
      dispatch(salesOrderApi.util.invalidateTags(['SalesOrder', 'SalesOrders']));
      message.success('Sales order was successfully updated!');
      dispatch(setSubmitLoadingMessage({ message: '', addCount: false }))

      if (close) {
        navigate('/');
      }
    } catch (err) {
      message.error((err as { data: { errorMessage: string } }).data.errorMessage ?? 'Failed to save sales order.', 5);
      console.log(err)
    }
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    if (pathname.includes('/draft')) {
      await handleUpdateSalesOrder(close);
    } else {
      await handleSaveSalesOrder(close);
    }
  };

  /* ****************** JSX / Variables ****************** */
  const menuItems = [
    {
      label: <div onClick={(): Promise<void> => handleSave(false)}>Save</div>,
      key: '1'
    }
  ]

  /* ****************** Render ****************** */
  return (
    <Tooltip title={!values.customerId || !values.customerPo ? 'Customer Id & Customer PO required to save' : undefined}>
      <Dropdown.Button
        type="primary"
        onClick={(): Promise<void> => handleSave(true)}
        disabled={!values.customerId || !values.customerPo || pathname.includes('/orders/') || pathname.includes('/posted')}
        loading={isSubmitting || isLoading || isUpdating}
        menu={{ items: menuItems }}>
        Save and close
      </Dropdown.Button>
    </Tooltip>
  );
};
